import {
  PublicClientApplication,
  AccountInfo,
  InteractionRequiredAuthError
} from '@azure/msal-browser'
import { msalConfig, loginRequest, tokenRequest } from '../config/msal'

export interface AuthUser {
  id: string
  name: string
  email: string
  roles: string[]
}

export interface AuthError {
  code: string
  message: string
}

function decodeJwtClaims(token: string): Record<string, unknown> {
  const payload = token.split('.')[1]
  return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')))
}

class AuthService {
  private msalInstance: PublicClientApplication | null = null
  private initialized = false
  private initPromise: Promise<void> | null = null
  private apiRoles: string[] = []

  async initialize(): Promise<void> {
    if (this.initialized) return
    if (this.initPromise) return this.initPromise

    this.initPromise = this.doInitialize()
    return this.initPromise
  }

  private async doInitialize(): Promise<void> {
    try {
      this.msalInstance = new PublicClientApplication(msalConfig)
      await this.msalInstance.initialize()

      // Handle redirect callback
      const response = await this.msalInstance.handleRedirectPromise()
      if (response) {
        this.msalInstance.setActiveAccount(response.account)
        // Extract roles from the access token (roles are on the API app, not the SPA)
        if (response.accessToken) {
          const claims = decodeJwtClaims(response.accessToken)
          this.apiRoles = (claims.roles as string[]) || []
        }
      } else {
        const accounts = this.msalInstance.getAllAccounts()
        if (accounts.length > 0) {
          this.msalInstance.setActiveAccount(accounts[0])
          // Try to get roles from a silent token request
          try {
            const tokenResponse = await this.msalInstance.acquireTokenSilent({
              ...tokenRequest,
              account: accounts[0]
            })
            if (tokenResponse.accessToken) {
              const claims = decodeJwtClaims(tokenResponse.accessToken)
              this.apiRoles = (claims.roles as string[]) || []
            }
          } catch {
            // Roles will be empty until next interactive login
          }
        }
      }

      this.initialized = true
    } catch (error) {
      console.error('MSAL initialization failed:', error)
      throw error
    }
  }

  private getMsalInstance(): PublicClientApplication {
    if (!this.msalInstance) {
      throw new Error('AuthService not initialized. Call initialize() first.')
    }
    return this.msalInstance
  }

  async login(): Promise<void> {
    const msal = this.getMsalInstance()

    try {
      // Store return URL before redirecting
      const returnUrl = window.location.pathname + window.location.search
      sessionStorage.setItem('auth_return_url', returnUrl)

      await msal.loginRedirect(loginRequest)
    } catch (error) {
      console.error('Login failed:', error)
      throw error
    }
  }

  async logout(): Promise<void> {
    const msal = this.getMsalInstance()

    try {
      await msal.logoutRedirect()
    } catch (error) {
      console.error('Logout failed:', error)
      throw error
    }
  }

  getAccount(): AccountInfo | null {
    if (!this.msalInstance) return null
    return this.msalInstance.getActiveAccount()
  }

  isAuthenticated(): boolean {
    return !!this.getAccount()
  }

  async getAccessToken(): Promise<string | null> {
    const msal = this.getMsalInstance()
    const account = this.getAccount()

    if (!account) return null

    try {
      const response = await msal.acquireTokenSilent({
        ...tokenRequest,
        account
      })
      return response.accessToken
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // Token expired or needs refresh via interaction
        try {
          await msal.acquireTokenRedirect(tokenRequest)
          return null // Will redirect
        } catch (redirectError) {
          console.error('Token redirect failed:', redirectError)
          throw redirectError
        }
      }
      console.error('Token acquisition failed:', error)
      throw error
    }
  }

  getUser(): AuthUser | null {
    const account = this.getAccount()
    if (!account) return null

    return {
      id: account.localAccountId,
      name: account.name || '',
      email: account.username || '',
      roles: this.apiRoles
    }
  }

  getReturnUrl(): string {
    const returnUrl = sessionStorage.getItem('auth_return_url')
    sessionStorage.removeItem('auth_return_url')
    return returnUrl || '/admin'
  }

  parseAuthError(urlParams: URLSearchParams): AuthError | null {
    const error = urlParams.get('error')
    const errorDescription = urlParams.get('error_description')

    if (!error) return null

    return {
      code: error,
      message: errorDescription || 'Authentication failed'
    }
  }
}

export const authService = new AuthService()
